import { isExpectedAutohighlightsError } from '@confluence/content-unified-query/entry-points/isExpectedAutohighlightsError.ts';
import {
	classifyContentPermissionError,
	isContentAutohighlightsError,
	isUnsupportedCustomerAccountFieldError,
} from '@confluence/content-unified-query/entry-points/errors.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import {
	ContentBlogUnifiedQueryArtifact,
	ContentUnifiedQueryArtifact,
	ContentUnifiedWithSpaceKeyQueryArtifact,
	createContentBlogUnifiedQueryVariables,
	createContentUnifiedQueryVariables,
	createContentUnifiedWithSpaceKeyQueryVariables,
	type ContentBlogUnifiedQueryType,
	type ContentBlogUnifiedQueryVariablesType,
	type ContentUnifiedQueryType,
	type ContentUnifiedQueryVariablesType,
	type ContentUnifiedWithSpaceKeyQueryType,
	type ContentUnifiedWithSpaceKeyQueryVariablesType,
} from '@confluence/content-unified-query';
import { expVal, isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { isUnauthorizedError } from '@confluence/query-preloader-tools/entry-points/isUnauthorizedError.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';
import { ssrPermissionsErrorHandler } from '@confluence/ssr-utilities/entry-points/SSRPermissionsErrorHandler.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';

type PreloadContentParameters = {
	contentId: string;
	isBlog: boolean;
	spaceKey: string;
	shouldPreloadUsingSpaceKey?: boolean;
	versionOverride?: number | null;
	userId?: string | null;
	agentSessionsEnabled?: boolean;
	autohighlightsQueryVariables?: {
		entityAri: string;
		workspaceId: string;
	};
	// Page-scoped managed (external) labels. When present, warms the
	// `useExternalLabels_query` spread on `ContentUnifiedQuery` (page variant only).
	externalLabelsQueryVariables?: {
		cloudId: string;
		ari: string;
	};
	rovoActivationQueryVariables?: {
		cloudId: string;
	};
	includeRendererAssetsDsp?: boolean;
	rendererAssetsDspAction?: 'ATTACHMENT_DOWNLOAD' | 'PAGE_EXPORT';
	hasDocumentContractAnalyticsByline?: boolean;
	hasDocumentContractContentHeader?: boolean;
	includeRendererAssetsMedia?: boolean;
	includeRendererAssetsCustomChrome?: boolean;
	/** Set by `preloadAccessLinkContent`. Not derived from the URL, so it is SSR-safe. */
	isAccessLinkRoute?: boolean;
	/** Raw eligibility signal used to exclude analytics byline fields when the experiment is enabled. */
	hasAnalyticsBylineGateReason?: boolean;
};

type PreloadContentResult = {
	result?: any;
	hasErrors: boolean;
};

type ChooseContentUnifiedQueryVariablesType =
	| ContentUnifiedQueryVariablesType
	| ContentBlogUnifiedQueryVariablesType;

type PreloadGraphQLError = Parameters<typeof isUnsupportedCustomerAccountFieldError>[0] & {
	message?: string;
};

/** A 403 on the root `space` field only nulls that field; the content still comes back. */
const isSpaceScopedError = (error: PreloadGraphQLError): boolean => error.path?.[0] === 'space';

const isContentScopedError = (error: PreloadGraphQLError): boolean => error.path?.[0] === 'content';

const isPathlessError = (error: PreloadGraphQLError): boolean => !error.path?.length;

/** Preserve page access when only known optional fields reject the viewer. */
function filterContentFieldErrors<
	T extends { errors?: ReadonlyArray<PreloadGraphQLError>; hasErrors: boolean },
>(
	result: T,
	requestedContentId: string | null | undefined,
	resolvedContentId: string | null | undefined,
): T {
	// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
	if (result.errors?.length && fg('confluence_shared_content_permission_errors')) {
		const errors = result.errors.filter((error) => {
			if (isContentAutohighlightsError(error, requestedContentId, resolvedContentId)) {
				if (!isExpectedAutohighlightsError(error)) {
					// Preserve visibility of the optional failure without failing the page preload.
					ssrPrepareErrorsClient.submitError(error, {
						attribution: 'autohighlights',
						path: error.path,
						extensions: error.extensions,
					});
				}
				markErrorAsHandled(error);
				return false;
			}
			if (
				classifyContentPermissionError(error, requestedContentId, resolvedContentId) ===
				'optional-permission'
			) {
				markErrorAsHandled(error);
				return false;
			}
			return true;
		});
		return { ...result, errors, hasErrors: errors.length > 0 };
	}
	if (
		!requestedContentId ||
		requestedContentId !== resolvedContentId ||
		!result.errors?.length ||
		!expVal('confluence_exp_vp_document', 'isEnabled', false)
	) {
		return result;
	}
	const errors = result.errors.filter((error) => {
		if (isUnsupportedCustomerAccountFieldError(error)) {
			markErrorAsHandled(error);
			return false;
		}
		return true;
	});
	return { ...result, errors, hasErrors: errors.length > 0 };
}

function processContentPreloadPermissionError(result: any, isAccessLinkRoute?: boolean) {
	result?.errors?.forEach((error: any) => {
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (fg('confluence_shared_content_permission_errors')) {
			// Optional failures have already been filtered with the requested content identity.
			const scope = classifyContentPermissionError(error, undefined, undefined);
			if (
				(scope === 'content-permission' || scope === 'unscoped-permission') &&
				!(isAccessLinkRoute && isSpaceScopedError(error))
			) {
				ssrPermissionsErrorHandler.markSSRPreloadPermissionError('view', error);
			}
			return;
		}
		// The "Cannot find space" condition is handled by redirecting to the correct space
		// even if error is not propagated to ContentPrerequisites where it is normally handled.
		if (isUnauthorizedError(error)) {
			// An access-link viewer has no space membership by design, so a space-scoped 403 must
			// not blank a page whose content resolved.
			if (isAccessLinkRoute && isSpaceScopedError(error)) {
				return;
			}

			// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
			if (fg('confluence_preload_perm_error_path_check')) {
				if (isContentScopedError(error) || isPathlessError(error)) {
					ssrPermissionsErrorHandler.markSSRPreloadPermissionError('view', error);
				}

				return;
			}

			ssrPermissionsErrorHandler.markSSRPreloadPermissionError('view', error);
		}
	});
}

export const transformContentData = (
	data: any,
): {
	content: {
		nodes: any[];
		// @ts-ignore
		__typename: string;
	} | null;
	space: any;
} => ({
	content: data?.spaceHomepage
		? {
				nodes: [data?.spaceHomepage],
				// @ts-ignore
				__typename: 'PaginatedContentListWithChild',
			}
		: null,
	space: data?.space || null,
});

export function preloadContent({
	contentId,
	spaceKey,
	isBlog,
	versionOverride = null,
	userId,
	agentSessionsEnabled = false,
	autohighlightsQueryVariables,
	externalLabelsQueryVariables,
	rovoActivationQueryVariables,
	includeRendererAssetsDsp = false,
	rendererAssetsDspAction,
	hasDocumentContractAnalyticsByline = false,
	hasDocumentContractContentHeader = false,
	includeRendererAssetsMedia = false,
	includeRendererAssetsCustomChrome = false,
	isAccessLinkRoute = false,
	hasAnalyticsBylineGateReason = false,
}: PreloadContentParameters): Promise<PreloadContentResult> {
	window.performance.mark('CFP-63.preloadContent.start');
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	const isSSR = Boolean(process.env.REACT_SSR);
	const shouldUseContentHeaderFragment =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		hasDocumentContractContentHeader &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_header_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false);
	const shouldUseFabricEligibilityFragment =
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		shouldUseContentHeaderFragment &&
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		!StatsigConfigurations.isKillSwitchOn('confluence_vp_fabric_eligibility_frag_reader') &&
		expVal('confluence_exp_vp_document', 'isEnabled', false);

	// Space overview
	if (spaceKey && !contentId) {
		// Keep includeBylineContributors disabled for both this fetch and the cache write below.
		// The homepage ID is not known yet, so the space-key query has no root contentContributors
		// field. Enabling it on the page-query write would require data this preload cannot supply.
		return query<ContentUnifiedWithSpaceKeyQueryType, ContentUnifiedWithSpaceKeyQueryVariablesType>(
			{
				query: ContentUnifiedWithSpaceKeyQueryArtifact,
				variables: {
					...createContentUnifiedWithSpaceKeyQueryVariables({
						userId,
						spaceKey,
						versionOverride,
						isSSR,
						includeRendererAssetsMedia,
						includeRendererAssetsCustomChrome,
						agentSessionsUserId: agentSessionsEnabled ? userId : undefined,
						// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
						includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
						includeConnectionAncestors: isExperimentEnabled(
							'confluence_1p_and_3p_connection_byline_experiment',
						),
						includeMetadata:
							!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
							expVal('confluence_exp_vp_document', 'isEnabled', false),
						includeClassification:
							!StatsigConfigurations.isKillSwitchOn(
								'confluence_vp_classification_context_frag_reader',
							) && expVal('confluence_exp_vp_document', 'isEnabled', false),
						includeReactions:
							!StatsigConfigurations.isKillSwitchOn('confluence_vp_reactions_frag_reader') &&
							expVal('confluence_exp_vp_document', 'isEnabled', false),
						includeAudioContent:
							!StatsigConfigurations.isKillSwitchOn('confluence_vp_audio_frag_reader') &&
							expVal('confluence_exp_vp_document', 'isEnabled', false),
						autohighlightsQueryVariables,
					}),
					...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_mode_frag_reader') &&
					expVal('confluence_exp_vp_document', 'isEnabled', false)
						? { useContentModeFragment: true }
						: {}),
				},
				errorPolicy: 'all',
			},
		).then((result: any) => {
			// Record the end mark and timestamp to track *both* duration of preloading
			// and timestamp of the moment when preloading finished
			window.performance.mark('CFP-63.preloadContent.end');
			window.performance.mark('CFP-63.preloadContentEnd');

			const data = result?.data;
			const homepageId = data?.spaceHomepage?.id;
			result = filterContentFieldErrors(result, homepageId, homepageId);
			if (homepageId) {
				// update result with transformed data
				result.data = transformContentData(data);

				writeQueryInterop<ContentUnifiedQueryType, ContentUnifiedQueryVariablesType>({
					query: ContentUnifiedQueryArtifact,
					variables: {
						...createContentUnifiedQueryVariables({
							userId,
							contentId: homepageId,
							spaceKey,
							versionOverride,
							isSSR,
							includeRendererAssetsMedia,
							includeRendererAssetsCustomChrome,
							agentSessionsUserId: agentSessionsEnabled ? userId : undefined,
							// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
							includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
							includeConnectionAncestors: isExperimentEnabled(
								'confluence_1p_and_3p_connection_byline_experiment',
							),
							includeMetadata:
								!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
								expVal('confluence_exp_vp_document', 'isEnabled', false),
							includeClassification:
								!StatsigConfigurations.isKillSwitchOn(
									'confluence_vp_classification_context_frag_reader',
								) && expVal('confluence_exp_vp_document', 'isEnabled', false),
							includeReactions:
								!StatsigConfigurations.isKillSwitchOn('confluence_vp_reactions_frag_reader') &&
								expVal('confluence_exp_vp_document', 'isEnabled', false),
							includeAudioContent:
								!StatsigConfigurations.isKillSwitchOn('confluence_vp_audio_frag_reader') &&
								expVal('confluence_exp_vp_document', 'isEnabled', false),
							autohighlightsQueryVariables,
						}),
						...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_mode_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false)
							? { useContentModeFragment: true }
							: {}),
					},
					data: { ...result.data },
				});
			}

			processContentPreloadPermissionError(result, isAccessLinkRoute);

			return { result, hasErrors: result?.hasErrors };
		});
	}

	const chooseContentUnifiedQuery = isBlog
		? ContentBlogUnifiedQueryArtifact
		: ContentUnifiedQueryArtifact;
	const queryVariables: ChooseContentUnifiedQueryVariablesType = isBlog
		? {
				...createContentBlogUnifiedQueryVariables({
					userId,
					contentId,
					spaceKey,
					versionOverride,
					isSSR,
					includeRendererAssetsMedia,
					includeRendererAssetsCustomChrome,
					preloadLabels: isSSR,
					// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
					includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
					includeBylineContributors:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
					includeAutomationDiscoveries: fg('cc_automations_discovery_nudges'),
					includeMetadata:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeClassification:
						!StatsigConfigurations.isKillSwitchOn(
							'confluence_vp_classification_context_frag_reader',
						) && expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeReactions:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_reactions_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeAudioContent:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_audio_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeAnalyticsByline:
						hasDocumentContractAnalyticsByline &&
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_analytics_byline_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false) &&
						(!hasAnalyticsBylineGateReason ||
							!expVal('confluence_analytics_byline_permission_gate', 'isEnabled', false)),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeContentHeader: shouldUseContentHeaderFragment,
					includeSmartCreateFeedback:
						shouldUseContentHeaderFragment &&
						!versionOverride &&
						!StatsigConfigurations.isKillSwitchOn(
							'confluence_vp_smart_create_banner_frag_reader',
						) &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeContentHeaderAccess:
						shouldUseContentHeaderFragment &&
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeFabricEditorEligibility: shouldUseFabricEligibilityFragment,
					includeContentHeaderPublicLink:
						shouldUseContentHeaderFragment &&
						!isBlog &&
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeRendererAssetsDsp,
					rendererAssetsDspAction,
					autohighlightsQueryVariables,
				}),
				...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_mode_frag_reader') &&
				expVal('confluence_exp_vp_document', 'isEnabled', false)
					? { useContentModeFragment: true }
					: {}),
			}
		: {
				...createContentUnifiedQueryVariables({
					userId,
					contentId,
					spaceKey,
					versionOverride,
					isSSR,
					includeRendererAssetsMedia,
					includeRendererAssetsCustomChrome,
					preloadLabels: isSSR,
					agentSessionsUserId: agentSessionsEnabled ? userId : undefined,
					// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
					includeAgentTrustReceiptData: fg('cc_sparks_agent_trust_receipt'),
					includeBylineContributors:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeConnectionAncestors: isExperimentEnabled(
						'confluence_1p_and_3p_connection_byline_experiment',
					),
					// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
					includeAutomationDiscoveries: fg('cc_automations_discovery_nudges'),
					includeMetadata:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_meta_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeClassification:
						!StatsigConfigurations.isKillSwitchOn(
							'confluence_vp_classification_context_frag_reader',
						) && expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeReactions:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_reactions_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeAudioContent:
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_audio_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeAnalyticsByline:
						hasDocumentContractAnalyticsByline &&
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_analytics_byline_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false) &&
						(!hasAnalyticsBylineGateReason ||
							!expVal('confluence_analytics_byline_permission_gate', 'isEnabled', false)),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeContentHeader: shouldUseContentHeaderFragment,
					includeSmartCreateFeedback:
						shouldUseContentHeaderFragment &&
						!versionOverride &&
						!StatsigConfigurations.isKillSwitchOn(
							'confluence_vp_smart_create_banner_frag_reader',
						) &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeContentHeaderAccess:
						shouldUseContentHeaderFragment &&
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					includeFabricEditorEligibility: shouldUseFabricEligibilityFragment,
					includeContentHeaderPublicLink:
						shouldUseContentHeaderFragment &&
						!isBlog &&
						!StatsigConfigurations.isKillSwitchOn('confluence_vp_access_frag_reader') &&
						expVal('confluence_exp_vp_document', 'isEnabled', false),
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					includeRendererAssetsDsp,
					rendererAssetsDspAction,
					autohighlightsQueryVariables,
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					externalLabelsQueryVariables,
					// @ts-ignore -- Downstream packages are checked against the last generated content-unified-query declaration.
					rovoActivationQueryVariables,
				}),
				...(!StatsigConfigurations.isKillSwitchOn('confluence_vp_mode_frag_reader') &&
				expVal('confluence_exp_vp_document', 'isEnabled', false)
					? { useContentModeFragment: true }
					: {}),
			};

	return query<
		ContentUnifiedQueryType | ContentBlogUnifiedQueryType,
		ChooseContentUnifiedQueryVariablesType
	>({
		query: chooseContentUnifiedQuery,
		variables: queryVariables,
		errorPolicy: 'all',
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr -- This controls request-scoped cache retention, not rendered output.
		...(!process.env.REACT_SSR &&
		expVal('confluence_relay_vp_preload_retention', 'isEnabled', false)
			? {
					relayQueryPreloadRetention: 'environment',
				}
			: {}),
	}).then((result: any) => {
		// Record the end mark and timestamp to track *both* duration of preloading
		// and timestamp of the moment when preloading finished
		window.performance.mark('CFP-63.preloadContent.end');
		window.performance.mark('CFP-63.preloadContentEnd');

		result = filterContentFieldErrors(result, contentId, result?.data?.content?.nodes?.[0]?.id);
		if (
			expVal('confluence_exp_vp_document', 'isEnabled', false) &&
			expVal('confluence_frontend_ssr_space_not_found', 'isEnabled', false)
		) {
			// The document route can omit ContentTypesHeaderQuery, including its restriction
			// classification side effect. Short links obfuscate the URL's space key, so use
			// the content-scoped error to retain the real denial reason before rendering.
			const isSpaceRestricted = result?.errors?.some(
				(error: PreloadGraphQLError & { message?: string }) =>
					isContentScopedError(error) && error.message?.includes('Space is restricted'),
			);
			window['__SPACE_RESTRICTED_ERROR__'] = isSpaceRestricted ? contentId : null;
		}
		processContentPreloadPermissionError(result, isAccessLinkRoute);

		return { result, hasErrors: result?.hasErrors };
	});
}
